import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Loader2 } from 'lucide-react'
import { useEffect, useRef, useState, useSyncExternalStore } from 'react'

import { getJob } from '@/api/jobs'
import { keys } from '@/api/keys'
import { subscribeJob } from '@/api/sse'
import { partsReadyLine, streamReady, type StoryRow, type StoryStream } from '@/api/stories'
import { cn } from '@/components/ui/utils'
import { formatClock } from '@/lib/format'
import type { Job, JobEvent } from '@/schema'

/**
 * The progress of a story being made, in plain words and REAL phases: the story job's `phase`
 * (catalog/rig → scenes → compile → voices → bundle) and, when the job leaves the voices to a
 * narration job (`tts.bake`), that job too. The page never fakes a percentage: a step is done when
 * the job says so.
 *
 * The job is followed through its live event stream (`/api/jobs/{id}/events`), not by polling it:
 * a phase, a part written or a part published shows the moment it happens. A slow poll stays as
 * the safety net for a stream that cannot connect. A longer story streams (docs/STORY-STREAMING.md):
 * once its first part is published (`row.stream.parts_ready ≥ 1`) it can be watched while the rest
 * is still being written.
 */

export const STORY_PHASES = [
  { id: 'cast', label: 'Getting the puppets ready' },
  { id: 'write', label: 'Writing the story' },
  { id: 'stage', label: 'Putting it on stage' },
  { id: 'voices', label: 'Recording the voices' },
] as const
export type StoryPhaseId = (typeof STORY_PHASES)[number]['id']

const PHASE_WORDS: Array<[StoryPhaseId, readonly string[]]> = [
  ['cast', ['catalog', 'cast', 'rig', 'autorig', 'puppet', 'queued', 'waiting']],
  ['write', ['scene', 'outline', 'write', 'story', 'vision', 'prompt', 'model']],
  ['stage', ['compile', 'bundle', 'bake', 'place', 'stage']],
  ['voices', ['tts', 'voice', 'narrat', 'audio']],
]

/** Which friendly step a job phase name belongs to, or null when it is not one we know. */
export function phaseStep(phase: string | null | undefined): number | null {
  if (!phase) return null
  const p = phase.toLowerCase()
  for (const [id, words] of PHASE_WORDS) if (words.some((w) => p.startsWith(w) || p.includes(w))) return STORY_PHASES.findIndex((s) => s.id === id)
  return null
}

const TERMINAL: ReadonlySet<Job['status']> = new Set(['succeeded', 'failed', 'cancelled'])
const TERMINAL_EVENTS: ReadonlySet<string> = TERMINAL

/**
 * How often the story job is still asked for while its event stream is followed. Only the safety
 * net for a stream that cannot connect (a proxy that buffers it, a flaky link), so it can be slow.
 */
export const JOB_SAFETY_POLL_MS = 5_000

export interface StoryProgressState {
  /** Index into `STORY_PHASES` of the step running now (`STORY_PHASES.length` = all done). */
  step: number
  /** The job's own parent-facing sentence, when it has one — after "Parts 1–2 of 7 are ready" once a part can be watched. */
  msg: string | null
  /** Waiting for the provider, not actively generating. */
  paused: boolean
  /** The story job ended badly although the row still says `generating` (a stuck row). */
  jobFailed: string | null
  /**
   * Play it: the story is written and its narration is finished (or not coming), or — a longer
   * story still being made — its first part is published, its job is known to be alive, and the
   * rest follows while it plays.
   */
  playable: boolean
  /**
   * A story still being made has part 1 out, but its job has not answered yet: it may be playable,
   * or dead. A job lost with its worker (a restart) leaves its row `generating` with part 1 out for
   * good, so the part alone never says the story is alive.
   */
  jobPending: boolean
  /** Part 1 of the story can be watched although it may still be being made (`streamReady`). */
  streamReady: boolean
  /** The row's part-by-part stream; null for a story that never streamed. */
  stream: StoryStream | null
}

export interface StoryProgressOptions {
  /**
   * Follow the story job live (default true). Off while the player covers the page: nothing here
   * is on screen then, and an open event stream would hold one of the six connections a browser
   * allows per host, which the player's loader wants for the pictures. The job is still asked for
   * then, slowly (`JOB_SAFETY_POLL_MS`), so a job that dies under the player can end it.
   */
  follow?: boolean
}

/**
 * How long the page waits for the voices before it plays with the reading voice: 90 s without any
 * news from the narration job — every line it records starts the wait again (a long story has
 * dozens) — and never more than 6 minutes in all.
 */
const VOICE_WAIT_MS = 90_000
const VOICE_WAIT_MAX_MS = 6 * 60_000

/** The newest `progress` a job's event stream reported, and when it came. */
interface LiveProgress {
  jobId: number
  at: number
  phase: string | null
  msg: string | null
}

const onVisibility = (cb: () => void): (() => void) => {
  document.addEventListener('visibilitychange', cb)
  return () => document.removeEventListener('visibilitychange', cb)
}
const pageVisible = (): boolean => document.visibilityState !== 'hidden'

/**
 * Follow a job's event stream while `jobId` is set, and keep its newest `progress`. `onEvent` sees
 * every event (the newest closure, not the first). A hidden tab lets go of the stream; shown
 * again, it picks up after the last event it saw instead of replaying the whole job.
 */
function useLiveProgress(jobId: number | null, onEvent: (ev: JobEvent) => void): LiveProgress | null {
  const [live, setLive] = useState<LiveProgress | null>(null)
  const handler = useRef(onEvent)
  handler.current = onEvent
  const seen = useRef<{ jobId: number; seq: number } | null>(null)
  useEffect(() => {
    if (jobId == null) return
    const after = seen.current?.jobId === jobId ? seen.current.seq : 0
    return subscribeJob(
      jobId,
      (ev) => {
        if (ev.seq > 0) seen.current = { jobId, seq: ev.seq }
        if (ev.kind === 'progress') setLive({ jobId, at: Date.now(), phase: ev.data.phase ?? null, msg: ev.data.msg ?? null })
        handler.current(ev)
      },
      after > 0 ? { lastEventId: String(after) } : {},
    )
  }, [jobId])
  return live?.jobId === jobId ? live : null
}

/**
 * How many parts an event says are published, when it says so: `parts_ready` on any event (the
 * part writer's `progress`, and the publisher's own "part k of N is ready to watch" log, the one
 * event that lands the moment a part is out: `story/stream.py`), or that log's `k` (an api from
 * before it carried `parts_ready`). The row is then fetched at once instead of at the next poll.
 */
export function publishedPart(ev: Pick<JobEvent, 'kind' | 'data'>): number | null {
  const d = ev.data as Record<string, unknown>
  if (typeof d['parts_ready'] === 'number') return d['parts_ready']
  if (ev.kind === 'log' && typeof d['k'] === 'number' && d['level'] !== 'warning' && d['level'] !== 'error') return d['k']
  return null
}

/** "15:45" for an ISO time, in the parent's own clock; null when there is none. */
function clockTime(iso: string | null | undefined): string | null {
  return !iso || Number.isNaN(Date.parse(iso)) ? null : formatClock(iso)
}

/**
 * The line under the steps while a streamed story is made: what can be watched already, then what
 * the job is doing now ("Parts 1–2 of 7 are ready. Writing part 3 of 7: The river…").
 */
export function streamingMsg(stream: StoryStream | null, jobMsg: string | null): string | null {
  const ready = partsReadyLine(stream)
  if (!ready || !stream || stream.state === 'failed' || stream.state === 'complete') return jobMsg
  if (stream.state === 'paused') {
    const at = clockTime(stream.resume_at)
    return `${ready}. The rest is made later${at ? ` (at ${at})` : ''}.`
  }
  return jobMsg ? `${ready}. ${jobMsg}` : `${ready}.`
}

/**
 * Follow the story's job (and its narration job) while the page is open: the job through its
 * event stream, with a slow safety poll; stops by itself. An event that says the row moved on —
 * a part published past the one the row shows, the job done, paused or ended — refetches the row
 * at once, so the player opens the moment part 1 is out instead of at the page's next poll.
 */
export function useStoryProgress(story: StoryRow | undefined, { follow = true }: StoryProgressOptions = {}): StoryProgressState {
  const qc = useQueryClient()
  const uid = story?.uid ?? null
  const jobId = story?.job_id ?? null
  const generating = story?.status === 'generating'
  const ready = story?.status === 'ready'
  const stream = story?.stream ?? null
  const canWatch = streamReady(story)
  const visible = useSyncExternalStore(onVisibility, pageVisible, () => true)
  const job = useQuery({
    queryKey: keys.jobs.detail(jobId ?? 0),
    queryFn: ({ signal }) => getJob(jobId as number, signal),
    enabled: jobId != null && (generating || ready),
    // Generating, the event stream brings the news and this is only its safety net — also under
    // the player, where nothing else learns that the job died (cancelled, or lost with its worker)
    // without the bundle saying so. Ready, the job ends moments after the row, and its result says
    // whether a narration job follows.
    refetchInterval: (q) => (q.state.data && TERMINAL.has(q.state.data.status) ? false : ready ? 1_000 : JOB_SAFETY_POLL_MS),
    retry: false,
  })
  const jobEnded = !!job.data && TERMINAL.has(job.data.status)
  // The job has answered since this page opened (or its cached answer is fresh and not being asked
  // again): only then does a published part make a story still being made playable. Once known it
  // stays known; `jobFailed` below follows every later answer.
  const jobSeen = useRef<number | null>(null)
  if (jobId != null && (job.isFetchedAfterMount || job.isError || (!!job.data && !job.isFetching))) jobSeen.current = jobId
  const jobKnown = jobId == null || jobSeen.current === jobId
  // A ready story is followed only in the moment its job is still ending (a stored story's job ended long ago).
  const following = (generating || (ready && !!job.data && !jobEnded)) && follow && visible
  const live = useLiveProgress(following ? jobId : null, (ev) => {
    if (uid == null || jobId == null) return
    const ended = TERMINAL_EVENTS.has(ev.kind)
    const published = publishedPart(ev)
    // Past the part the row shows — or any part, over a stream the row still shows as failed (a
    // story being made again publishes from its own start).
    const newPart = published != null && published >= 1 && (stream?.state === 'failed' || published > (stream?.parts_ready ?? 0))
    const rowMoved = ended || newPart || (ev.kind === 'progress' && (ev.data.phase === 'done' || ev.data.phase === 'paused'))
    if (ended) void qc.invalidateQueries({ queryKey: keys.jobs.detail(jobId) })
    if (rowMoved) void qc.invalidateQueries({ queryKey: keys.stories.detail(uid) })
  })
  // The freshest of the two: the stream's newest event, or the safety poll's job row.
  const fromStream = live != null && live.at >= job.dataUpdatedAt
  const phase = fromStream ? live.phase : (job.data?.progress?.phase ?? null)
  const jobMsg = fromStream ? live.msg : (job.data?.progress?.msg ?? null)
  const ttsJobId = typeof job.data?.result?.['tts_job_id'] === 'number' ? (job.data.result['tts_job_id'] as number) : null
  const tts = useQuery({
    queryKey: keys.jobs.detail(ttsJobId ?? 0),
    queryFn: ({ signal }) => getJob(ttsJobId as number, signal),
    enabled: ready && ttsJobId != null,
    refetchInterval: (q) => (q.state.data && TERMINAL.has(q.state.data.status) ? false : 1_000),
    retry: false,
  })

  // The furthest step seen so far: phases only move forward on screen.
  const furthest = useRef(0)
  const fromJob = phaseStep(phase) ?? (job.data?.status === 'queued' ? 0 : null)
  if (fromJob != null && fromJob > furthest.current) furthest.current = fromJob

  // Narration: wait for it (bounded), so the story starts with voices. Progress restarts the wait.
  const [waitedOut, setWaitedOut] = useState(false)
  const readySince = useRef<number | null>(null)
  const ttsNews = `${tts.data?.progress?.pct ?? ''}|${tts.data?.progress?.msg ?? ''}`
  useEffect(() => {
    if (!ready) return
    if (readySince.current == null) readySince.current = Date.now()
    const left = Math.max(0, VOICE_WAIT_MAX_MS - (Date.now() - readySince.current))
    const t = setTimeout(() => setWaitedOut(true), Math.min(VOICE_WAIT_MS, left))
    return () => clearTimeout(t)
  }, [ready, ttsNews])

  const jobFailed = generating && job.data && (job.data.status === 'failed' || job.data.status === 'cancelled') ? job.data.error || 'The story could not be finished.' : null

  if (ready) {
    const voicesPending = ttsJobId != null && !(tts.data && TERMINAL.has(tts.data.status)) && !tts.isError && !waitedOut
    // The story job itself is still settling (its result names the narration job, if any): wait
    // for it too. The row turns ready a moment BEFORE the job ends, so a job seen running is not
    // yet the answer — only an ended one is.
    const storyJobSettling = jobId != null && !jobEnded && !job.isError && !waitedOut
    const playable = !voicesPending && !storyJobSettling
    return { step: playable ? STORY_PHASES.length : 3, msg: voicesPending ? (tts.data?.progress?.msg ?? null) : null, paused: false, jobFailed: null, playable, jobPending: false, streamReady: canWatch, stream }
  }
  // Still being made: "Recording the voices" is the job's own last step now (a streamed story
  // records them inside it), and a published first part is enough to start watching — once the job
  // is known to be alive. A stream that says `failed` on a row still `generating` is a story about
  // to say so, or one being made again (an operator's retry) that has not re-published yet.
  const watchable = generating && canWatch && stream?.state !== 'failed'
  return {
    step: Math.min(furthest.current, 3),
    msg: streamingMsg(stream, jobMsg),
    paused: phase === 'paused' || (phase == null && stream?.state === 'paused'),
    jobFailed,
    playable: watchable && jobKnown && !jobFailed,
    jobPending: watchable && !jobKnown,
    streamReady: canWatch,
    stream,
  }
}

export function StoryPhases({ step, msg, paused = false }: { step: number; msg: string | null; paused?: boolean }) {
  return (
    <div className="flex flex-col gap-4">
      <ol className="flex flex-col gap-2" aria-label="Progress" data-testid="story-phases">
        {STORY_PHASES.map((p, i) => {
          const done = i < step
          const now = i === step && !paused
          return (
            <li key={p.id} className={cn('flex min-h-11 items-center gap-3 rounded-xl px-3', now && 'bg-accent-soft')} aria-current={now ? 'step' : undefined} data-state={done ? 'done' : now ? 'now' : 'todo'}>
              <span
                className={cn(
                  'inline-flex size-7 shrink-0 items-center justify-center rounded-full border',
                  done ? 'border-transparent bg-ok text-white' : now ? 'border-transparent bg-accent text-accent-ink' : 'border-line text-ink-3',
                )}
                aria-hidden
              >
                {done ? <Check className="size-4" /> : now ? <Loader2 className="size-4 animate-spin" /> : <span className="text-xs">{i + 1}</span>}
              </span>
              <span className={cn('text-base', done ? 'text-ink-2' : now ? 'font-semibold text-ink' : 'text-ink-3')}>
                {p.label}
                {now ? '…' : ''}
              </span>
            </li>
          )
        })}
      </ol>
      <p className="min-h-5 text-sm text-ink-2" role="status" aria-live="polite">
        {msg ?? ''}
      </p>
    </div>
  )
}
